import { useState, useEffect, useRef } from 'react';
import { X, Upload, Loader2, AlertTriangle, RotateCw } from 'lucide-react';
import {
  useCampaignProducts,
  useCreateProduct,
  useUpdateProduct,
  usePresignProductPhoto,
} from '@/hooks/useCampaignCatalog';
import { CatalogMultiSelect, type CatalogItem } from './CatalogMultiSelect';
import { api } from '@/lib/api';

interface Props {
  campaignId: string;
  productId: string | null;
  onClose: () => void;
}

export function ProductDrawer({ campaignId, productId, onClose }: Props) {
  const { data: products, isError: productsError, refetch: retryLoad } = useCampaignProducts(campaignId);
  const createMutation = useCreateProduct(campaignId);
  const updateMutation = useUpdateProduct(campaignId);
  const presignMutation = usePresignProductPhoto(campaignId);

  const existing = productId ? products?.find((p) => p.id === productId) : null;
  // Editando um produto que a busca nao trouxe: o formulario fica em branco e o
  // `canSubmit` bloqueia o salvar (nada e sobrescrito), mas sem um aviso isso
  // se parece com "o produto perdeu os dados".
  const loadFailed = !!productId && productsError && !existing;

  const [name, setName] = useState(existing?.name ?? '');
  const [sku, setSku] = useState(existing?.sku ?? '');
  const [price, setPrice] = useState(existing ? String(Number(existing.price)) : '');
  const [buyUrl, setBuyUrl] = useState(existing?.buyUrl ?? '');
  const [deliveryMin, setDeliveryMin] = useState(existing?.deliveryDaysMin?.toString() ?? '');
  const [deliveryMax, setDeliveryMax] = useState(existing?.deliveryDaysMax?.toString() ?? '');
  const [active, setActive] = useState(existing?.active ?? true);
  const [outOfStock, setOutOfStock] = useState(existing?.outOfStock ?? false);
  const [photoUrl, setPhotoUrl] = useState(existing?.photoUrl ?? '');
  const [photoS3Key, setPhotoS3Key] = useState(existing?.photoS3Key ?? '');
  const [uploading, setUploading] = useState(false);
  const [selectedModels, setSelectedModels] = useState<CatalogItem[]>([]);
  const [modelsLoadFailed, setModelsLoadFailed] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setSku(existing.sku ?? '');
      setPrice(String(Number(existing.price)));
      setBuyUrl(existing.buyUrl);
      setDeliveryMin(existing.deliveryDaysMin?.toString() ?? '');
      setDeliveryMax(existing.deliveryDaysMax?.toString() ?? '');
      setActive(existing.active);
      setOutOfStock(existing.outOfStock);
      setPhotoUrl(existing.photoUrl ?? '');
      setPhotoS3Key(existing.photoS3Key ?? '');

      // Load compatible models from IDs
      const ids = existing.compatibleModelIds;
      if (ids && ids.length > 0) {
        setModelsLoadFailed(false);
        api.get('/vehicle-catalog/by-ids', { params: { ids: ids.join(',') } })
          .then((res) => {
            setSelectedModels((res.data as { items: CatalogItem[] }).items);
            setModelsLoadFailed(false);
          })
          // Engolir esta falha fazia o formulario exibir a compatibilidade
          // VAZIA de um produto que tem N modelos — o parceiro lia como
          // "perdi os dados". (Salvar dali nao apaga nada: com a lista vazia o
          // cliente manda `undefined` e a API so grava o campo quando ele vem
          // definido. Mas a tela nao tem por que mentir.)
          .catch(() => setModelsLoadFailed(true));
      } else {
        setSelectedModels([]);
        setModelsLoadFailed(false);
      }
    }
  }, [existing]);

  async function handlePhotoUpload(file: File) {
    setUploading(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() as 'jpg' | 'jpeg' | 'png' | 'webp';
      const { uploadUrl, s3Key } = await presignMutation.mutateAsync(ext || 'jpg');

      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      setPhotoS3Key(s3Key);
      // Local preview only — signed URL comes from API on next list fetch
      setPhotoUrl(URL.createObjectURL(file));
    } finally {
      setUploading(false);
    }
  }

  function handleSubmit() {
    const modelIds = selectedModels.length > 0 ? selectedModels.map((m) => m.id) : undefined;
    const data = {
      name,
      sku: sku || undefined,
      photoS3Key: photoS3Key || undefined,
      price: parseFloat(price),
      buyUrl,
      deliveryDaysMin: deliveryMin ? parseInt(deliveryMin) : undefined,
      deliveryDaysMax: deliveryMax ? parseInt(deliveryMax) : undefined,
      compatibleModelIds: modelIds,
      active,
      outOfStock,
    };

    if (productId) {
      updateMutation.mutate({ productId, ...data }, { onSuccess: onClose });
    } else {
      createMutation.mutate(data, { onSuccess: onClose });
    }
  }

  // `loadFailed` entra no gate porque o aviso PROMETE que salvar esta
  // bloqueado — e sem isto a promessa era falsa. Nao e so coerencia de texto:
  // `active` e `outOfStock` sao booleanos com default (`true`/`false`), entao
  // eles SEMPRE vao no payload. Salvar a partir do formulario em branco
  // reativaria um produto pausado e limparia o "fora de estoque", sem que o
  // parceiro tenha tocado em nenhum dos dois.
  const canSubmit =
    !loadFailed && name.trim().length > 0 && parseFloat(price) > 0 && buyUrl.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      {/* Drawer */}
      <div className="relative z-10 flex h-full w-full max-w-lg flex-col overflow-y-auto bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-lg font-bold">
            {productId ? 'Editar produto' : 'Novo produto'}
          </h2>
          <button aria-label="Fechar" onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 space-y-4 p-4">
          {loadFailed && (
            <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3" role="alert">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-destructive" />
              <div className="flex-1 text-xs">
                <p className="font-semibold text-destructive">Não foi possível carregar este produto</p>
                <p className="mt-0.5 text-muted-foreground">
                  Os campos estão em branco porque a busca falhou, não porque o produto
                  perdeu os dados. Salvar está bloqueado até eles carregarem.
                </p>
                <button
                  type="button"
                  onClick={() => void retryLoad()}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 font-medium transition-colors hover:bg-muted"
                >
                  <RotateCw size={12} />
                  Tentar de novo
                </button>
              </div>
            </div>
          )}
          {/* Photo */}
          <div>
            <label className="mb-1 block text-sm font-medium">Foto do produto</label>
            <div
              className="flex h-32 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-border hover:bg-muted"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : photoUrl ? (
                <img src={photoUrl} alt="Preview" className="h-full rounded-lg object-contain" />
              ) : (
                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                  <Upload size={20} />
                  <span className="text-xs">Clique para enviar</span>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handlePhotoUpload(file);
              }}
            />
          </div>

          {/* Name */}
          <div>
            <label className="mb-1 block text-sm font-medium">Nome *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Pneu Michelin 205/55 R16"
              className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
            />
          </div>

          {/* SKU */}
          <div>
            <label className="mb-1 block text-sm font-medium">SKU</label>
            <input
              type="text"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="Opcional"
              className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
            />
          </div>

          {/* Price */}
          <div>
            <label className="mb-1 block text-sm font-medium">Preço (R$) *</label>
            <input
              type="number"
              min={0.01}
              step={0.01}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
            />
          </div>

          {/* Buy URL */}
          <div>
            <label className="mb-1 block text-sm font-medium">URL de compra *</label>
            <input
              type="url"
              value={buyUrl}
              onChange={(e) => setBuyUrl(e.target.value)}
              placeholder="https://loja.com/produto/..."
              className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
            />
          </div>

          {/* Delivery */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Prazo mínimo (dias)</label>
              <input
                type="number"
                min={0}
                value={deliveryMin}
                onChange={(e) => setDeliveryMin(e.target.value)}
                className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Prazo máximo (dias)</label>
              <input
                type="number"
                min={0}
                value={deliveryMax}
                onChange={(e) => setDeliveryMax(e.target.value)}
                className="w-full rounded-lg border border-border bg-muted px-4 py-2.5 text-sm outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Compatible models */}
          <div>
            <label className="mb-1 block text-sm font-medium">Modelos compatíveis</label>
            <p className="mb-2 text-xs text-muted-foreground">
              Opcional. Selecione os modelos de carro compatíveis com este produto.
            </p>
            {modelsLoadFailed && (
              <p className="mb-2 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive" role="alert">
                Não foi possível carregar os modelos já cadastrados neste produto.
                Eles continuam salvos — recarregue a página antes de mexer na
                compatibilidade, para não substituí-los sem querer.
              </p>
            )}
            <CatalogMultiSelect selected={selectedModels} onChange={setSelectedModels} />
          </div>

          {/* Toggles */}
          <div className="space-y-2">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-primary"
              />
              <span className="text-sm">Produto ativo</span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={outOfStock}
                onChange={(e) => setOutOfStock(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-primary"
              />
              <span className="text-sm">Fora de estoque</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || isPending}
            className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-opacity disabled:opacity-50"
          >
            {isPending ? 'Salvando...' : productId ? 'Salvar alterações' : 'Criar produto'}
          </button>
        </div>
      </div>
    </div>
  );
}
